import CategoryIcon from "@components/CategoryIcon";
import { Typography } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import {
  PropsWithChildren,
  Ref,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { DisplayTypes } from "@/config";
import { Comparison, DataPoint, TCarbonUnit } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";
import {
  extractEndYear,
  extractHeaders,
  extractRows,
  extractStartYear,
} from "@/utils/comparison";

import ComparisonLoading from "./ComparisonLoading";
import ComparisonTableExport from "./ComparisonTableExport";

export function ComparisonTableHeader({ headers }: { headers: string[] }) {
  return (
    <thead>
      <tr>
        <th scope="col"></th>
        {headers.map((header) => (
          <Typography
            key={header}
            weight="semibold"
            step="-2"
            as="th"
            className="py-xs"
          >
            {header}
          </Typography>
        ))}
      </tr>
    </thead>
  );
}

function ComparisonTableRow({
  rowKey,
  variant = "default",
  onHover,
  children,
}: PropsWithChildren<{
  rowKey: string;
  variant?: "default" | "highlighted";
  onHover: (name: string | undefined) => void;
}>) {
  return (
    <tr
      className={cn(
        "hover:bg-grayscale-xx-light hover:cursor-pointer",
        variant === "highlighted" && "bg-grayscale-xx-light"
      )}
      onMouseEnter={() => onHover(rowKey)}
      onMouseLeave={() => onHover(undefined)}
    >
      {children}
    </tr>
  );
}

function ComparisonTableBody({
  rows,
  unit,
  highlightedRow,
  onHover,
}: {
  rows: DataPoint[][];
  unit: TCarbonUnit;
  highlightedRow?: string;
  onHover: (name: string | undefined) => void;
}) {
  const { t } = useTranslation(["common", "reviewCategories", "formatter"]);
  const size = rows[0].length;

  return (
    <>
      {[...Array(size).keys()].map((index) => (
        <ComparisonTableRow
          key={rows[0][index].key}
          rowKey={rows[0][index].key}
          onHover={onHover}
          variant={
            rows[0][index].key === highlightedRow ? "highlighted" : "default"
          }
        >
          <Typography step="-2" weight="semibold" as="td" className="py-2xs">
            <CategoryIcon name={rows[0][index].key} className="pr-2xs inline" />
            {t(`reviewCategories:${rows[0][index].key}`)}
          </Typography>
          {rows.map((row: DataPoint[]) => {
            const { formattedCarbon } = formatCarbon(
              t,
              row[index].carbon,
              unit
            );

            return (
              <Typography
                key={row[index].id}
                step="-2"
                as="td"
                weight="semibold"
                className="py-2xs text-grayscale"
              >
                {formattedCarbon}
                &nbsp;
                {t(`common:unit.${unit}`)}
              </Typography>
            );
          })}
        </ComparisonTableRow>
      ))}
    </>
  );
}

function ComparisonTableTotals({
  totals,
  unit,
}: {
  totals: DataPoint[][];
  unit: TCarbonUnit;
}) {
  const { t } = useTranslation(["reviewCategories", "common"]);

  return (
    <>
      <tr key="Totals">
        <Typography step="-2" weight="bold" as="td" className="py-2xs">
          <CategoryIcon name="total" className="pr-2xs inline" />
          {t("reviewCategories:total")}
        </Typography>
        {totals.map((row: DataPoint[]) => {
          const { formattedCarbon } = formatCarbon(t, row[0].carbon, unit);

          return (
            <Typography
              key={row[0].id}
              step="-2"
              as="td"
              weight="semibold"
              className="py-2xs text-grayscale"
            >
              {formattedCarbon}
              &nbsp;
              {t(`common:unit.${unit}`)}
            </Typography>
          );
        })}
      </tr>
    </>
  );
}

type ComparisonTableProps = {
  data: Comparison;
  type: DisplayTypes;
  onHover: (name: string | undefined) => void;
  ref?: Ref<ComparisonTableRef>;
};

export type ComparisonTableRef = {
  highlight: (name: string | undefined) => void;
};

function ComparisonTable({ data, type, onHover, ref }: ComparisonTableProps) {
  const tableRef = useRef<HTMLTableElement>(null);

  const [highlightedRow, setHighlightedRow] = useState<string | undefined>(
    undefined
  );

  useImperativeHandle(ref, () => {
    return {
      highlight(name: string | undefined) {
        setHighlightedRow(name);
      },
    };
  });

  if (!data) {
    return <ComparisonLoading />;
  }

  const headers = extractHeaders(data);
  const rows = extractRows(data.columns, type);

  const totals = extractRows(data.columns, DisplayTypes.total);

  // We calculate the unit from the totals since these are the largest values displayed.
  const dataUnit = getCarbonDisplayUnit(
    totals.flat().map((total) => total.carbon)
  );

  return (
    <div className="">
      <div className="flow-root">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table
              ref={tableRef}
              className="divide-grayscale-x-light min-w-full divide-y"
            >
              <ComparisonTableHeader headers={headers} />
              <tbody className="divide-grayscale-x-light divide-y">
                {type !== DisplayTypes.total ? (
                  <ComparisonTableTotals totals={totals} unit={dataUnit} />
                ) : null}
                <ComparisonTableBody
                  rows={rows}
                  onHover={onHover}
                  highlightedRow={highlightedRow}
                  unit={dataUnit}
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <ComparisonTableExport
        type={type}
        totals={totals}
        headers={headers}
        rows={rows}
        startYear={extractStartYear(data)}
        endYear={extractEndYear(data)}
      />
    </div>
  );
}

ComparisonTable.displayName = "ComparisonTable";

export default ComparisonTable;
